import { BehaviorSubject, fromEvent, Subject, Subscription } from 'rxjs';
import { ElementRef, Injectable, OnDestroy, signal } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class LayoutService implements OnDestroy {
    private _isCollapsedSubject = new BehaviorSubject<boolean>(false);
    private _isHidden = false;
    private _onScroll = new Subject<Event>();
    private _headerHidden = new BehaviorSubject<boolean>(false);

    onScroll$ = this._onScroll.asObservable();

    private _scrollContainer!: HTMLElement;
    private _scrollSubscription?: Subscription;
    private _menuVisible = signal(false);
    private _pullDownRefreshAction?: (event: Subject<void>) => void;
    private _scrollToRefreshContainer!: HTMLElement;

    get headerHidden() {
        return this._headerHidden.value;
    }

    set scrollContainer(container: HTMLElement) {
        this._scrollContainer = container;
        this.listenForScrollEvents();
    }

    get scrollContainer() {
        return this._scrollContainer;
    }

    set scrollToRefreshContainer(container: HTMLElement) {
        this._scrollToRefreshContainer = container;
    }

    get scrollToRefreshContainer() {
        return this._scrollToRefreshContainer;
    }

    get isHidden() {
        if (localStorage.getItem('isMenuHidden') !== null) {
            this._isHidden = localStorage.getItem('isMenuHidden') === 'true';
        }
        return this._isHidden;
    }

    set isHidden(value) {
        localStorage.setItem('isMenuHidden', value.toString());
        this._isHidden = value;
    }

    get isCollapsed() {
        return this._isCollapsedSubject.value;
    }

    set isCollapsed(value) {
        this._isCollapsedSubject.next(value);
    }

    get pullDownRefreshAction() {
        return this._pullDownRefreshAction ?? (() => window.location.reload());
    }

    isCollapsed$ = this._isCollapsedSubject.asObservable();

    get menuVisible() {
        return this._menuVisible();
    }

    ngOnDestroy(): void {
        this._scrollSubscription?.unsubscribe();
    }

    private listenForScrollEvents() {
        this._scrollSubscription?.unsubscribe();
        this._scrollSubscription = fromEvent(
            this.scrollContainer,
            'scroll'
        ).subscribe(event => this._onScroll.next(event));
    }

    openMenu() {
        this._menuVisible.set(true);
    }

    closeMenu() {
        this._menuVisible.set(false);
    }

    hideHeader() {
        this._headerHidden.next(true);
    }

    showHeader() {
        this._headerHidden.next(false);
    }

    setPullDownRefreshAction(
        action: (event: Subject<void>) => void,
        targetElement?: ElementRef<HTMLElement>
    ) {
        if (targetElement) {
            this.scrollToRefreshContainer = targetElement.nativeElement;
        } else {
            this.scrollToRefreshContainer = this.scrollContainer;
        }
        this._pullDownRefreshAction = action;
    }

    clearPullDownRefreshAction() {
        this._pullDownRefreshAction = undefined;
        this.scrollToRefreshContainer = this.scrollContainer;
    }
}
